import api from 'api';
import { ColumnService } from 'lib/services/directory';
import { logAndCaptureException } from 'utils';
import {
  RefundUIAdapter,
  RefundUIConfig,
  RefundDisplayItem,
  RefundUIAdapterArgs,
  PerformRefundContext
} from '../types';

export const orderRefundAdapter: RefundUIAdapter = {
  getLineItemsDiff() {
    // No diff logic yet for orders (future enhancement after edits diffing defined)
    return [];
  },
  getDefaultRefundAmount() {
    return 0; // start at 0 per requirements
  },
  getDisplayItems({ entity }: RefundUIAdapterArgs): RefundDisplayItem[] {
    if (entity.kind !== 'order') return [];
    const list = entity.newspaperOrders ?? [];
    return list.map(no => ({
      id: no.id,
      description: `${no.newspaperName} — ${no.publishingDates.join(', ')} — ${
        no.publishingMedium
      }`,
      amountInCents: no.pricingTotalInCents - no.totalRefundInCents
    }));
  },
  buildUIConfig({ permissions }): RefundUIConfig {
    const { canRefund, canVoid } = permissions;
    const refundDisabled = !(canRefund || canVoid);
    return {
      header: 'Refund Invoice?',
      body: `The advertiser has already paid for this order. Enter per-item refund amounts on the left to initiate a refund.`,
      refundDisabled,
      perItemRefund: true,
      performRefund: async (ctx: PerformRefundContext) => {
        if (ctx.args.entity.kind !== 'order') return;
        const newspaperOrderRefunds = (ctx.items || [])
          .map(item => {
            if (!item.id) {
              return null;
            }
            return {
              newspaperOrderId: item.id,
              amountInCents: item.amountInCents
            };
          })
          .filter(
            (
              item
            ): item is { newspaperOrderId: string; amountInCents: number } =>
              item !== null
          );
        const payload = {
          orderId: ctx.args.entity.id,
          refundReason: ctx.reason,
          newspaperOrderRefunds
        };
        const { error } = await api.safePost<'orders/refund'>(
          'orders/refund',
          payload
        );
        if (error) {
          logAndCaptureException(
            ColumnService.PAYMENTS,
            error,
            'Failed to refund order invoice',
            {
              orderId: ctx.args.entity.id,
              refundCount: String(newspaperOrderRefunds.length)
            }
          );
          throw error;
        }
      }
    };
  },
  buildDisplaySubtitle({ entity }) {
    if (entity.kind !== 'order') return '';
    const base = `Paid invoice for ${entity.id}`;
    return entity.title ? `${base} ("${entity.title}")` : base;
  }
};
